import { User, Post, Community, Conversation, Message, NotificationItem } from '../types';

let currentCsrfToken: string | null = null;
let currentSessionToken: string | null = localStorage.getItem('cordle_token');

export function setSessionToken(token: string | null, csrfToken?: string) {
  currentSessionToken = token;
  if (token) {
    localStorage.setItem('cordle_token', token);
  } else {
    localStorage.removeItem('cordle_token');
  }
  if (csrfToken) currentCsrfToken = csrfToken;
}

export function getSessionToken(): string | null {
  return currentSessionToken;
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers || {});
  headers.set('Content-Type', 'application/json');

  if (currentSessionToken) {
    headers.set('Authorization', `Bearer ${currentSessionToken}`);
  }
  if (currentCsrfToken && ['POST', 'PUT', 'DELETE'].includes(options.method || 'GET')) {
    headers.set('x-csrf-token', currentCsrfToken);
  }

  try {
    const res = await fetch(endpoint, { ...options, headers });
    const text = await res.text();
    let data: any = {};
    if (text && text.trim().length > 0) {
      try {
        data = JSON.parse(text);
      } catch {
        data = { message: text };
      }
    }

    if (!res.ok) {
      throw new Error(data?.error || data?.message || `HTTP error ${res.status}`);
    }
    return data as T;
  } catch (err: any) {
    console.error(`[Cordle API Error] ${endpoint}:`, err);
    throw err;
  }
}

export const api = {
  // Auth
  getMe: () => request<{ authenticated: boolean; user: User | null; csrfToken?: string }>('/api/auth/me'),
  register: (data: any) => request<any>('/api/auth/register', { method: 'POST', body: JSON.stringify(data) }),
  login: (data: any) => request<any>('/api/auth/login', { method: 'POST', body: JSON.stringify(data) }),
  logout: () => request<any>('/api/auth/logout', { method: 'POST' }),
  verify2FA: (userId: string, code: string) => request<any>('/api/auth/verify-2fa', { method: 'POST', body: JSON.stringify({ userId, code }) }),
  setup2FA: () => request<any>('/api/auth/setup-2fa', { method: 'POST' }),
  enable2FA: (code: string) => request<any>('/api/auth/enable-2fa', { method: 'POST', body: JSON.stringify({ code }) }),
  forgotPassword: (email: string) => request<any>('/api/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),
  resetPassword: (resetToken: string, newPassword: string) => request<any>('/api/auth/reset-password', { method: 'POST', body: JSON.stringify({ resetToken, newPassword }) }),

  // Users & Profiles
  getProfile: (username: string) => request<{ user: User; posts: Post[]; mediaPosts: Post[]; likedPosts: Post[] }>(`/api/users/${username}`),
  updateProfile: (data: Partial<User>) => request<{ user: User; message: string }>('/api/users/profile', { method: 'PUT', body: JSON.stringify(data) }),
  toggleFollow: (userId: string) => request<{ isFollowing: boolean }>(`/api/users/${userId}/follow`, { method: 'POST' }),
  toggleFriend: (userId: string) => request<{ status: string; message: string }>(`/api/users/${userId}/friend`, { method: 'POST' }),
  toggleBlock: (userId: string) => request<{ isBlocked: boolean }>(`/api/users/${userId}/block`, { method: 'POST' }),
  toggleMute: (userId: string) => request<{ isMuted: boolean }>(`/api/users/${userId}/mute`, { method: 'POST' }),

  // Posts
  getFeed: (type: string = 'home', query?: string) => request<{ posts: Post[]; trendingTags: { tag: string; count: number }[] }>(`/api/posts/feed?type=${type}${query ? `&q=${encodeURIComponent(query)}` : ''}`),
  createPost: (data: any) => request<{ message: string; post: Post }>('/api/posts', { method: 'POST', body: JSON.stringify(data) }),
  getPost: (id: string) => request<{ post: Post; replies: Post[] }>(`/api/posts/${id}`),
  toggleLike: (id: string) => request<{ isLiked: boolean; likesCount: number }>(`/api/posts/${id}/like`, { method: 'POST' }),
  toggleRepost: (id: string) => request<{ isReposted: boolean; repostsCount: number }>(`/api/posts/${id}/repost`, { method: 'POST' }),
  toggleBookmark: (id: string) => request<{ isBookmarked: boolean }>(`/api/posts/${id}/bookmark`, { method: 'POST' }),
  deletePost: (id: string) => request<{ message: string }>(`/api/posts/${id}`, { method: 'DELETE' }),

  // Communities
  getCommunities: () => request<{ communities: Community[] }>('/api/communities'),
  getCommunity: (slug: string) => request<{ community: Community; posts: Post[] }>(`/api/communities/${slug}`),
  createCommunity: (data: any) => request<{ community: Community; message: string }>('/api/communities', { method: 'POST', body: JSON.stringify(data) }),
  toggleJoinCommunity: (id: string) => request<{ isJoined: boolean; membersCount: number }>(`/api/communities/${id}/join`, { method: 'POST' }),

  // Messages & Chat
  getConversations: () => request<{ conversations: Conversation[] }>('/api/messages/conversations'),
  createConversation: (recipientIds: string[], isGroup?: boolean, name?: string) => request<{ conversation: Conversation }>('/api/messages/conversations', { method: 'POST', body: JSON.stringify({ recipientIds, isGroup, name }) }),
  getMessages: (id: string) => request<{ conversation: Conversation; messages: Message[] }>(`/api/messages/conversations/${id}`),
  sendMessage: (id: string, content: string, mediaUrl?: string) => request<{ message: Message }>(`/api/messages/conversations/${id}`, { method: 'POST', body: JSON.stringify({ content, mediaUrl }) }),

  // Notifications
  getNotifications: () => request<{ notifications: NotificationItem[]; unreadCount: number }>('/api/notifications'),
  markAllNotificationsRead: () => request<{ message: string }>('/api/notifications/read-all', { method: 'POST' }),

  // Media Upload (File from PC or Data URL)
  uploadMedia: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch('/api/media/upload', {
      method: 'POST',
      body: formData,
    });
    const text = await res.text();
    let data: any = {};
    if (text && text.trim().length > 0) {
      try {
        data = JSON.parse(text);
      } catch {
        data = { message: text };
      }
    }
    if (!res.ok) {
      throw new Error(data?.error || 'Media upload failed');
    }
    return data as { url: string; mediaKey: string; type: string };
  },
};
